/**
 * Seed buckets for the admin Knowledge-Map Seed tool (issue #402).
 *
 * Mastery values (0 / 20 / 50 / 70) are deliberate placeholders, not calibrated
 * figures — they give the seed tool four distinguishable starting points until
 * real client data justifies tuning them. The stability map is conservative
 * (2 / 4 / 10 / 21 days) so that real attempt history overtakes the seed
 * quickly, rather than the seed dominating early recommendations.
 *
 * Problem difficulty bands are **not** part of seeding — the recommendation
 * engine will derive those from current mastery when ProblemSelector ships.
 */
export type SeedBucket =
  | 'knows_nothing'
  | 'beginner'
  | 'intermediate'
  | 'advanced'

export interface SeedBucketValues {
  /** 0–100 — starting `mastery` (EMA ceiling, FSRS DEV-3). */
  mastery: number
  /** FSRS stability in days. */
  stabilityDays: number
  /** FSRS difficulty, 1–10. */
  difficulty: number
  /** Human label for the UI. */
  label: string
  /** One-line description for the UI. */
  description: string
}

export const SEED_BUCKETS: Record<SeedBucket, SeedBucketValues> = {
  knows_nothing: {
    mastery: 0,
    stabilityDays: 2,
    difficulty: 5,
    label: 'Ground level',
    description: 'Fresh start — zero prior knowledge.',
  },
  beginner: {
    mastery: 20,
    stabilityDays: 4,
    difficulty: 5,
    label: 'Easy',
    description: 'Has seen the basics; needs lots of practice.',
  },
  intermediate: {
    mastery: 50,
    stabilityDays: 10,
    difficulty: 5,
    label: 'Intermediate',
    description: 'Comfortable on most topics; needs targeted reinforcement.',
  },
  advanced: {
    mastery: 70,
    stabilityDays: 21,
    difficulty: 5,
    label: 'Advanced',
    description: 'Strong baseline; preparing for high-bar interviews.',
  },
}

export const SEED_BUCKET_ORDER: readonly SeedBucket[] = [
  'knows_nothing',
  'beginner',
  'intermediate',
  'advanced',
] as const
